/**
 * Cost by Model Chart
 * Horizontal bar chart showing cost breakdown per LLM model
 */

import React from 'react';
import { Cpu } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { CostByModel } from '../types';

interface CostByModelChartProps {
  data: CostByModel[];
}

const BRAND_PRIMARY = '#03e8d3';

const MODEL_COLORS: Record<string, string> = {
  'claude-3-7-sonnet-20250219': '#8B5CF6',
  'claude-3-opus-20240229': '#A855F7',
  'claude-3-haiku-20240307': '#C084FC',
  'gpt-4o': '#10B981',
  'gpt-4o-mini': '#34D399',
  'gemini-2.5-pro': '#FBBF24',
  'deepseek-chat': '#3B82F6',
  'unknown': '#9CA3AF',
};

const getModelColor = (model: string): string => {
  for (const [key, color] of Object.entries(MODEL_COLORS)) {
    if (model.toLowerCase().includes(key.toLowerCase().split('-')[0])) {
      return color;
    }
  }
  return MODEL_COLORS['unknown'];
};

const formatCost = (value: number): string => {
  if (value >= 1) return `$${value.toFixed(2)}`;
  return `$${value.toFixed(4)}`;
};

const formatNumber = (value: number): string => {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return value.toLocaleString();
};

export const CostByModelChart: React.FC<CostByModelChartProps> = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Cpu className="w-5 h-5" style={{ color: BRAND_PRIMARY }} />
          <h3 className="text-lg font-semibold text-gray-900">Cost by Model</h3>
        </div>
        <div className="flex items-center justify-center h-48 text-gray-500">
          No model cost data available
        </div>
      </div>
    );
  }

  const totalCost = data.reduce((sum, d) => sum + d.cost, 0);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Cpu className="w-5 h-5" style={{ color: BRAND_PRIMARY }} />
            <h3 className="text-lg font-semibold text-gray-900">Cost by Model</h3>
          </div>
          <span className="text-lg font-bold" style={{ color: BRAND_PRIMARY }}>
            {formatCost(totalCost)}
          </span>
        </div>
      </div>

      <div className="p-4">
        <div style={{ height: Math.max(data.length * 40, 150) }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" horizontal={false} />
              <XAxis
                type="number"
                tick={{ fontSize: 10, fill: '#6B7280' }}
                tickFormatter={(value) => formatCost(value)}
                axisLine={{ stroke: '#E5E7EB' }}
              />
              <YAxis
                type="category"
                dataKey="model"
                tick={{ fontSize: 10, fill: '#6B7280' }}
                width={180}
                tickLine={false}
                axisLine={{ stroke: '#E5E7EB' }}
                tickFormatter={(value) => {
                  if (value.length > 28) return value.substring(0, 28) + '...';
                  return value;
                }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                }}
                formatter={(value: number, _name: string, props: any) => [
                  formatCost(value),
                  `Cost (${formatNumber(props.payload.requests)} requests)`,
                ]}
              />
              <Bar dataKey="cost" radius={[0, 4, 4, 0]}>
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getModelColor(entry.model)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default CostByModelChart;
